import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Box,
  Typography,
  Alert,
  Chip,
  TextField,
  CircularProgress,
  Tooltip
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { SelectedManifest } from '../utils/types';

interface DeleteManifestDialogProps {
  open: boolean;
  selectedManifest: SelectedManifest | null;
  isProcessing: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
}

export default function DeleteManifestDialog({
  open,
  selectedManifest,
  isProcessing,
  onClose,
  onConfirm
}: DeleteManifestDialogProps) {
  const [reasonDraft, setReasonDraft] = useState('');

  useEffect(() => {
    if (open) {
      setReasonDraft('');
    }
  }, [open]);

  const cancelDisabledHint = isProcessing ? 'Please wait until the current action finishes.' : '';
  const confirmDisabledHint = isProcessing
    ? 'Delete is in progress.'
    : !reasonDraft.trim()
      ? 'Reason for deletion is required.'
      : '';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 12px 40px rgba(0,0,0,0.15)'
        }
      }}>
      <DialogTitle
        sx={{
          background: 'linear-gradient(135deg, #d32f2f 0%, #c62828 100%)',
          color: '#fff',
          px: 3,
          py: 3
        }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              backgroundColor: 'rgba(255, 255, 255, 0.15)',
              display: 'flex',
              backdropFilter: 'blur(10px)'
            }}>
            <DeleteIcon sx={{ fontSize: 24, color: '#fff' }} />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={600} sx={{ color: '#fff' }}>
              Delete Manifest
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.85)', mt: 0.5 }}>
              Permanently remove this manifest
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ px: 3, py: 3, '& > :first-of-type': { mt: 1 } }}>
        <Stack spacing={3}>
          <Alert
            severity="warning"
            icon={<ErrorOutlineIcon />}
            sx={{
              borderRadius: 2,
              border: '1px solid rgba(237, 108, 2, 0.2)',
              backgroundColor: 'rgba(237, 108, 2, 0.04)',
              fontSize: '0.875rem',
              '& .MuiAlert-icon': {
                color: '#ed6c02'
              }
            }}>
            This action will permanently mark the manifest as deleted. This action cannot be undone.
          </Alert>

          <Box
            sx={{
              p: 2.5,
              borderRadius: 2,
              backgroundColor: 'rgba(211, 47, 47, 0.04)',
              border: '2px solid rgba(211, 47, 47, 0.15)'
            }}>
            <Stack spacing={2}>
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    textTransform: 'uppercase',
                    letterSpacing: 0.8,
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    color: '#d32f2f'
                  }}>
                  Manifest ID
                </Typography>
                <Typography variant="h6" fontWeight={600} sx={{ mt: 1, color: '#c62828' }}>
                  {selectedManifest?.manifest?.id}
                </Typography>
              </Box>
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    textTransform: 'uppercase',
                    letterSpacing: 0.8,
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    color: '#d32f2f'
                  }}>
                  Current Status
                </Typography>
                <Box sx={{ mt: 1 }}>
                  <Chip
                    label={selectedManifest?.tripDisplayStatus}
                    size="small"
                    sx={{
                      fontWeight: 600,
                      backgroundColor: 'rgba(0,0,0,0.06)',
                      color: 'text.secondary'
                    }}
                  />
                </Box>
              </Box>
            </Stack>
          </Box>

          <Box>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                color: 'text.primary',
                mb: 1.5,
                fontSize: '0.875rem'
              }}>
              Reason for Deletion{' '}
              <Box component="span" sx={{ color: 'error.main' }}>
                *
              </Box>
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              required
              value={reasonDraft}
              onChange={(e) => setReasonDraft(e.target.value)}
              placeholder="Explain why this manifest is being deleted (e.g., duplicate, erroneous entry, etc.)..."
              helperText="This reason will be logged in the audit trail"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  fontSize: '0.875rem'
                }
              }}
            />
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          py: 2.5,
          backgroundColor: 'rgba(0, 0, 0, 0.02)',
          borderTop: '1px solid rgba(0, 0, 0, 0.08)',
          gap: 1.5
        }}>
        <Tooltip title={cancelDisabledHint} disableHoverListener={!cancelDisabledHint} arrow>
          <span>
            <Button
              onClick={onClose}
              disabled={isProcessing}
              startIcon={isProcessing ? <CircularProgress size={16} color="inherit" /> : undefined}
              sx={{
                textTransform: 'none',
                fontWeight: 500,
                color: 'text.secondary',
                fontSize: '0.875rem',
                px: 2.5,
                py: 1
              }}>
              Cancel
            </Button>
          </span>
        </Tooltip>
        <Tooltip title={confirmDisabledHint} disableHoverListener={!confirmDisabledHint} arrow>
          <span>
            <Button
              variant="contained"
              onClick={() => onConfirm(reasonDraft)}
              disabled={!reasonDraft.trim() || isProcessing}
              startIcon={
                isProcessing ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon sx={{ fontSize: 18 }} />
              }
              sx={{
                px: 3,
                py: 1,
                fontWeight: 600,
                fontSize: '0.875rem',
                textTransform: 'none',
                backgroundColor: '#d32f2f',
                '&:hover': { backgroundColor: '#c62828' },
                boxShadow: 'none',
                borderRadius: 1.5
              }}>
              {isProcessing ? 'Deleting...' : 'Delete Manifest'}
            </Button>
          </span>
        </Tooltip>
      </DialogActions>
    </Dialog>
  );
}
